import type { Category, ISODate, Transaction } from './types';
import { isInMonth } from './dates';
import { signedAmount, totals } from './money';

export function activeActual(transactions: Transaction[]): Transaction[] {
  return transactions.filter((tx) => !tx.deletedAt && (tx.type === 'expense' || tx.type === 'refund'));
}

export function monthTransactions(transactions: Transaction[], month: ISODate): Transaction[] {
  return activeActual(transactions).filter((tx) => isInMonth(tx.eventDate, month));
}

export function categoryTotals(transactions: Transaction[], categories: Category[]): Array<{ category: Category; amountMinor: string }> {
  const sums = new Map<string, bigint>();
  for (const tx of activeActual(transactions)) {
    const direction = tx.type === 'refund' ? -1n : 1n;
    for (const allocation of tx.allocations) {
      sums.set(allocation.categoryId, (sums.get(allocation.categoryId) ?? 0n) + direction * BigInt(allocation.amountMinor));
    }
  }
  return categories.map((category) => ({ category, amountMinor: (sums.get(category.id) ?? 0n).toString() })).filter((row) => row.amountMinor !== '0').sort((a, b) => (BigInt(a.amountMinor) > BigInt(b.amountMinor) ? -1 : 1));
}

export function budgetSummary(transactions: Transaction[], totalMinor?: string | null) {
  const actual = totals(activeActual(transactions)).netMinor;
  return { actualMinor: actual, remainingMinor: totalMinor == null ? null : (BigInt(totalMinor) - BigInt(actual)).toString() };
}

export function overlayTransactions(confirmed: Transaction[], operations: Array<{ state: string; type: string; payload: unknown; createdAt?: string; opId?: string; predecessorOpId?: string | null; acknowledgedEntityVersion?: number }>): Transaction[] {
  const map = new Map(confirmed.map((tx) => [tx.id, tx]));
  const byId = new Map(operations.filter(op => op.opId).map(op => [op.opId!, op]));
  const ordered: typeof operations = [];
  const visited = new Set<(typeof operations)[number]>();
  const visit = (operation: (typeof operations)[number]) => {
    if (visited.has(operation)) return;
    visited.add(operation);
    const predecessor = operation.predecessorOpId ? byId.get(operation.predecessorOpId) : undefined;
    if (predecessor) visit(predecessor);
    ordered.push(operation);
  };
  [...operations].sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')).forEach(visit);
  for (const operation of ordered) {
    if (!['pending', 'sending', 'acknowledged'].includes(operation.state)) continue;
    if (operation.type === 'transaction.create' || operation.type === 'transaction.update') {
      const payload = operation.payload as Transaction;
      map.set(payload.id, operation.acknowledgedEntityVersion === undefined ? payload : { ...payload, version: operation.acknowledgedEntityVersion });
    }
    if (operation.type === 'transaction.delete' || operation.type === 'transaction.restore') {
      const payload = operation.payload as { id: string; deletedAt: string | null };
      const current = map.get(payload.id);
      if (current) map.set(payload.id, { ...current, deletedAt: payload.deletedAt, version: operation.acknowledgedEntityVersion ?? current.version });
    }
  }
  return [...map.values()].sort((a, b) => b.eventDate.localeCompare(a.eventDate) || b.createdAt.localeCompare(a.createdAt));
}

export function transactionContribution(transaction: Transaction): bigint {
  return signedAmount(transaction.type, transaction.amountMinor);
}
